import React, { useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Trash2, CircleOff } from "lucide-react";

export default function Messages() {
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [recipientType, setRecipientType] = useState("student_id");
  const [recipientValue, setRecipientValue] = useState("");
  const [messageText, setMessageText] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const coursesPerPage = 16;
  // const [hideInactive, setHideInactive] = useState(false);

  const [editMessageId, setEditMessageId] = useState(null);
  const [editMessageText, setEditMessageText] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);

  // --- ADDED STATE FOR STUDENT & CLASS FILTER/SEARCH ---
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [classSearchQuery, setClassSearchQuery] = useState('');


  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getMessage`);
      // Adjust depending on your backend
      const data = response.data.data || response.data;
      setMessages(data);
      console.log("Messages fetched:", data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };


  // Fetch students, classes, and existing messages
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studentResponse, classResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudent`),
          axios.get(`${process.env.REACT_APP_API_URL}/admin/getClass`),
        ]);
  
        setStudents(studentResponse.data.data || []);
        setClasses(classResponse.data.data || []);
        await fetchMessages();
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
  
    fetchData();
  }, []);
  
  const handleCreateMessage = () => setShowMessageForm(true);

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
  
    if (!messageText || (!recipientValue && recipientType !== "all")) {
      alert("Please fill out all required fields.");
      return;
    }
  
    setLoading(true);
  
    const formData = {
      recipient_type: recipientType,
      recipient_value: recipientValue,
      message_text: messageText,
    };
  
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/createMessage`, formData);
      if (response.status === 201) {
        alert("Message successfully created!");
  
        // ✅ Refresh message list immediately
        await fetchMessages();
  
        // Reset form
        setShowMessageForm(false);
        setRecipientType("student_id");
        setRecipientValue("");
        setMessageText("");
        setStudentSearchQuery("");
        setClassSearchQuery("");
      }
    } catch (error) {
      console.error("Error creating message:", error);
      alert("Failed to create message.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowMessageForm(false);
    setRecipientType("student_id");
    setRecipientValue("");
    setMessageText("");
    setStudentSearchQuery("");
    setClassSearchQuery("");
  };

  const getRecipientDisplay = (message) => {
    if (message.recipient_type === "all") return "All";
    if (message.recipient_type === "class_id") return `Class: ${message.class_id}`;
    if (message.recipient_type === "student_id") return `Student: ${message.student_id}`;
    return "Unknown";
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
    setCurrentPage(1); // reset to page 1 when searching
  };

  const filteredMessages = messages.filter((msg) => {
    const recipient = getRecipientDisplay(msg).toLowerCase();
    const text = msg.text.toLowerCase();
    return recipient.includes(searchQuery) || text.includes(searchQuery);
  });  

  const indexOfLast = currentPage * coursesPerPage;
  const indexOfFirst = indexOfLast - coursesPerPage;
  const currentMessages = filteredMessages.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredMessages.length / coursesPerPage);

  const handleManageMessage = (messageId) => {
    const messageToEdit = messages.find(msg => msg.message_id === messageId);
    if (!messageToEdit) return;
  
    setEditMessageId(messageId);
    setEditMessageText(messageToEdit.text);
    setShowEditModal(true);
  };
  
  const handleDelete = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
  
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/deleteMessage/${messageId}`);
      await fetchMessages(); 
    } catch (error) {
      console.error("Error deleting message:", error);
      alert("Failed to delete message.");
    }
  };
  
  const handleToggleActive = async (messageId) => {
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/admin/toggleMessageStatus/${messageId}`);
      const updatedMessages = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getMessage`);
      setMessages(updatedMessages.data.data);
    } catch (error) {
      console.error("Error toggling message status:", error);
    }
  };

  // --- FILTERED LISTS ---
  const filteredStudents = students.filter((student) => {
    const fullName = `${student.surname || ''} ${student.first_name || ''}`.toLowerCase();
    const studentIdStr = String(student.student_id || '').toLowerCase();
    const query = studentSearchQuery.toLowerCase();
    return fullName.includes(query) || studentIdStr.includes(query);
  });

  const filteredClasses = classes.filter((cls) => {
    const classNameStr = String(cls.class_name || '').toLowerCase();
    const classIdStr = String(cls.class_id || '').toLowerCase();
    const query = classSearchQuery.toLowerCase();
    return classNameStr.includes(query) || classIdStr.includes(query);
  });
  

  return (
    <div className="p-8 min-h-screen overflow-y-auto">
      <h1 className="text-2xl font-bold">Message Board</h1>
      <div className="bg-neutral-200 p-4">
        <div className="mt-2 mb-4 border-t border-gray-400 w-9/12 mx-auto"></div>
        <button
          onClick={handleCreateMessage}
          className="px-4 py-2 bg-blue-400 text-white rounded hover:bg-blue-500 absolute right-10"
        >
          Create Message
        </button>

        <div className="mb-4 flex flex-col md:flex-row md:items-center md:gap-4">
            <input
              type="text"
              placeholder="Search by content, Recipient , date"
              value={searchQuery}
              onChange={handleSearch}
              className="w-[300px] px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
        </div>

        <table className="w-full border-collapse bg-white mt-6">
          <thead>
            <tr>
              <th className="border px-4 py-2">Recipient</th>
              <th className="border px-4 py-2">Message</th>
              <th className="border px-4 py-2">Created At</th>
              <th className="border px-4 py-2">Status</th>
              <th className="border px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentMessages.map((message) => (
              <tr key={message.id}>
                <td className="border px-4 py-2">{getRecipientDisplay(message)}</td>
                <td className="border px-4 py-2">{message.text}</td>
                <td className="border px-4 py-2">{new Date(message.ctime).toLocaleString()}</td>
                <td className="border px-4 py-2">{message.status}</td>
                <td className="px-4 py-2 border">
                    <div className="flex justify-center gap-2">
                      <button 
                          onClick={() => handleManageMessage(message.message_id)}>
                          <Pencil className="w-5 h-5 text-blue-600 hover:text-blue-800" />
                      </button>
                      <button
                        onClick={() => handleDelete(message.message_id)}>  
                        <Trash2 className="w-5 h-5 text-red-600 hover:text-red-800" />
                      </button>
                      <button onClick={() => handleToggleActive(message.message_id)}>
                        <CircleOff
                          className={`w-5 h-5 ${
                            message.status === 'active' ? "text-green-600" : "text-gray-400"
                          } hover:text-green-800`}
                        />
                      </button>
                    </div>
                </td>
              </tr>
            ))}
            {currentMessages.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center py-4 text-gray-500">
                  No messages found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showMessageForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-1/2 max-w-lg rounded-lg shadow-lg p-6 relative">
            <h2 className="text-xl font-semibold mb-4">Create Message</h2>
            <form onSubmit={handleMessageSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-1">Select Recipient Type:</label>
                <select
                  value={recipientType}
                  onChange={(e) => {
                    setRecipientType(e.target.value);
                    setRecipientValue("");
                    setStudentSearchQuery("");
                    setClassSearchQuery("");
                  }}
                  className="border rounded w-full py-2 px-3"
                  required
                >
                  <option value="all">All</option>
                  <option value="class_id">Class</option>
                  <option value="student_id">Student</option>
                </select>
              </div>
              {recipientType !== "all" && (
                <div className="mb-4">
                  <label className="block text-gray-700 mb-1">
                    {recipientType === "class_id" ? "Select Class:" : "Select Student:"}
                  </label>
                  
                  {/* Class Search Filter Input */}
                  {recipientType === "class_id" && (
                    <input
                      type="text"
                      placeholder="Type class name or ID to filter..."
                      value={classSearchQuery}
                      onChange={(e) => setClassSearchQuery(e.target.value)}
                      className="border rounded w-full py-1.5 px-3 mb-2 text-sm text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  )}

                  {/* Student Search Filter Input */}
                  {recipientType === "student_id" && (
                    <input
                      type="text"
                      placeholder="Type name or ID to filter students..."
                      value={studentSearchQuery}
                      onChange={(e) => setStudentSearchQuery(e.target.value)}
                      className="border rounded w-full py-1.5 px-3 mb-2 text-sm text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  )}

                  <select
                    value={recipientValue}
                    onChange={(e) => setRecipientValue(e.target.value)}
                    className="border rounded w-full py-2 px-3"
                    required
                  >
                    <option value="" disabled>
                      Select a {recipientType === "class_id" ? "class" : "student"}
                    </option>
                    {recipientType === "class_id"
                      ? filteredClasses.map((cls) => (
                          <option key={cls.class_id} value={cls.class_id}>
                            {cls.class_name}
                          </option>
                        ))
                      : filteredStudents.map((student) => (
                          <option key={student.student_id} value={student.student_id}>
                            {`${student.surname} ${student.first_name} (${student.student_id})`}
                          </option>
                        ))}
                  </select>
                </div>
              )}
              <div className="mb-4">
                <label className="block text-gray-700 mb-1">Message:</label>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  className="border rounded w-full py-2 px-3"
                  rows="4"
                  required
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-gray-500 text-white px-4 py-2 mr-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 ${loading && "opacity-50 cursor-not-allowed"}`}
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showEditModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-1/2 max-w-lg rounded-lg shadow-lg p-6 relative">
            <h2 className="text-xl font-semibold mb-4">Edit Message</h2>
            <textarea
              value={editMessageText}
              onChange={(e) => setEditMessageText(e.target.value)}
              rows="4"
              className="border w-full p-2 rounded mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowEditModal(false)}
                className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.put(`${process.env.REACT_APP_API_URL}/admin/updateMessage/${editMessageId}`, {
                      message_text: editMessageText,
                    });
                    const updatedMessages = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getMessage`);
                    setMessages(updatedMessages.data.data);
                    setShowEditModal(false);
                    setEditMessageId(null);
                    setEditMessageText("");
                  } catch (error) {
                    console.error("Error updating message:", error);
                    alert("Failed to update message.");
                  }
                }}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Save
              </button>
            </div>
          </div>
        </div>
        )}


      {/* Pagination Controls */}
      <div className="flex justify-between items-center mt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <select
            value={currentPage}
            onChange={(e) => setCurrentPage(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <option key={page} value={page}>
                Page {page} of {totalPages}
              </option>
            ))}
          </select>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
    </div>
  );
}